import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, Camera, Ruler, ShieldCheck, Eye } from "lucide-react";

/**
 * DroneInspectionIntro — "The Pre-Visit"
 *
 * Full-viewport cinematic drone sequence with inspection-style annotation
 * overlays that TEACH clients what our crew evaluates before arriving on-site.
 * Reinforces: precision, methodology, "you're in the right hands."
 *
 * Video: royalty-free Pexels footage (commercial use, no attribution required).
 * Fallback: CSS/canvas cinematic if user has Save-Data on, reduced-motion, or
 * the video fails to load. Same educational overlays render either way.
 *
 * Placement: Before the ServiceCards section on the homepage — sets the
 * "premium precision" tone right as we transition from Why → What we do.
 */

/* Real Bespoke crew video (self-hosted) with Pexels drone as fallback */
const VIDEO_SOURCES = [
  "/brand/video/bespoke-boomlift-crew.mp4",
  // Fallback: royalty-free Pexels drone footage
  "https://videos.pexels.com/video-files/2406629/2406629-hd_1920_1080_24fps.mp4",
];

const POSTER = "/brand/video/bespoke-boomlift-crew-poster.jpg";

/* Inspection callouts — appear in sequence, teaching without being techy */
const CALLOUTS = [
  {
    at: 0,
    coord: "N 40°43'12\" · W 74°00'02\"",
    label: "Survey point · Approach angle",
    detail: "Building envelope indexed",
    pos: { top: "22%", left: "10%" },
  },
  {
    at: 3.5,
    coord: "Facade material",
    label: "Cast-iron + brick composite",
    detail: "Requires soft-wash · 0 PSI at joints",
    pos: { top: "48%", right: "12%" },
  },
  {
    at: 7,
    coord: "Sec. 02-N · Awning band",
    label: "Fabric assessment",
    detail: "UV fade + micro-soiling flagged",
    pos: { top: "70%", left: "16%" },
  },
  {
    at: 10.5,
    coord: "Sec. 04-E · Storefront glass",
    label: "Access · Ground-level WFP",
    detail: "No ladder · 0-TDS finish planned",
    pos: { top: "34%", right: "14%" },
  },
];

const INSPECTION_METHOD = [
  {
    Icon: Eye,
    n: "01",
    title: "We see the building before we touch it",
    body: "Every recurring client is drone-surveyed on onboarding. Facade material, access constraints, biological load — indexed before a crew ever arrives.",
  },
  {
    Icon: Ruler,
    n: "02",
    title: "Method is matched to material",
    body: "Cast iron gets soft-wash. Limestone gets DI water. Anodized panels get zero-detergent. We don't blast everything at 3,000 PSI because it's fast.",
  },
  {
    Icon: ShieldCheck,
    n: "03",
    title: "You get the same debrief we do",
    body: "Every visit closes with a written report + before/after imagery — same format as our internal ops brief. No black box.",
  },
];

export default function DroneInspectionIntro() {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [t, setT] = useState(0);

  // Respect prefers-reduced-motion + Save-Data
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    // Save-Data hint
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn?.saveData) setVideoFailed(true);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || videoFailed || reducedMotion) return;
    const onTime = () => setT(v.currentTime);
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [videoFailed, reducedMotion]);

  const showFallback = videoFailed || reducedMotion;

  // Cycle callouts based on video time (or on a timer in fallback)
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    if (showFallback) {
      const iv = setInterval(() => setActiveIdx((i) => (i + 1) % CALLOUTS.length), 3200);
      return () => clearInterval(iv);
    }
    // Live: pick the last callout whose `at` is ≤ current time
    let hit = 0;
    for (let i = 0; i < CALLOUTS.length; i++) if (t >= CALLOUTS[i].at) hit = i;
    setActiveIdx(hit);
  }, [t, showFallback]);

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: "#020204", color: "var(--text)" }}
      data-testid="drone-inspection"
    >
      {/* -------------- Cinematic canvas -------------- */}
      <div className="relative w-full" style={{ height: "min(88vh, 900px)" }}>
        {!showFallback && (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            src={VIDEO_SOURCES[0]}
            poster={POSTER}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onCanPlay={() => setVideoReady(true)}
            onError={() => setVideoFailed(true)}
            style={{ filter: "brightness(0.7) contrast(1.06) saturate(0.85)" }}
            data-testid="drone-inspection-video"
          />
        )}

        {/* Fallback CSS/canvas cinematic (parallax building silhouettes + dust) */}
        {showFallback && <FallbackCinematic />}

        {/* Vignette + grade */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: `
            radial-gradient(1200px 700px at 50% 40%, transparent 30%, rgba(0,0,0,0.55) 100%),
            linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 20%, transparent 55%, rgba(0,0,0,0.7) 100%)
          `,
        }} />

        {/* Scanline overlay — subtle "field camera" feel */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{
          backgroundImage: "repeating-linear-gradient(180deg, transparent 0px, transparent 2px, rgba(255,255,255,0.5) 3px)",
          mixBlendMode: "overlay",
        }} />

        {/* -------------- HUD frame -------------- */}
        {/* Corner registration marks (blueprint) */}
        {["tl", "tr", "bl", "br"].map((c) => {
          const isTop = c.startsWith("t"), isLeft = c.endsWith("l");
          return (
            <div key={c} className="absolute pointer-events-none" style={{
              [isTop ? "top" : "bottom"]: "24px",
              [isLeft ? "left" : "right"]: "24px",
              width: "36px", height: "36px",
              borderTop: isTop ? "1px solid var(--gold)" : "none",
              borderBottom: !isTop ? "1px solid var(--gold)" : "none",
              borderLeft: isLeft ? "1px solid var(--gold)" : "none",
              borderRight: !isLeft ? "1px solid var(--gold)" : "none",
              opacity: 0.5,
            }} />
          );
        })}

        {/* Top HUD bar */}
        <div className="absolute top-6 left-0 right-0 flex items-center justify-between px-8 md:px-12 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--gold-2)" }}>
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#4ade80" }} />
            <span>Bespoke Ops · Live crew feed</span>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <span>BOOM 32 FT</span>
            <span className="opacity-40">·</span>
            <span>OPS-{String(activeIdx + 1).padStart(3, "0")}</span>
            <span className="opacity-40">·</span>
            <span>REC ◉</span>
          </div>
        </div>

        {/* Bottom HUD — headline */}
        <div className="absolute bottom-0 left-0 right-0 px-8 md:px-14 pb-16 md:pb-24">
          <div className="max-w-[1600px] mx-auto">
            <div className="overline mb-4">The Pre-Visit</div>
            <h2 className="font-display" style={{
              fontSize: "clamp(2.6rem, 6.5vw, 5.6rem)",
              letterSpacing: "-0.035em",
              lineHeight: 0.95,
              maxWidth: "20ch",
            }}>
              We survey the building{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>before we ever knock.</span>
            </h2>
            <div className="mt-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-2)" }}>
              <ArrowDown size={12} />
              Scroll for what we look for
            </div>
          </div>
        </div>

        {/* -------------- Inspection callouts -------------- */}
        <AnimatePresence>
          {CALLOUTS.map((c, i) => (
            i === activeIdx && (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95, y: 6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="absolute z-10 hidden md:block"
                style={{ ...c.pos }}
                data-testid={`drone-callout-${i}`}
              >
                {/* Reticle */}
                <div className="relative w-14 h-14">
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      border: "1px solid var(--gold)",
                      boxShadow: "0 0 20px rgba(198,168,124,0.4)",
                    }}
                    animate={{ scale: [1, 1.25, 1], opacity: [1, 0.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <div className="absolute inset-4 rounded-full" style={{ border: "1px solid var(--gold-2)" }} />
                  <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-px" style={{ background: "var(--gold)", opacity: 0.7 }} />
                  <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px" style={{ background: "var(--gold)", opacity: 0.7 }} />
                </div>
                {/* Label card */}
                <div className="mt-2 px-4 py-2.5 min-w-[220px]" style={{
                  background: "rgba(2,2,4,0.72)",
                  border: "1px solid var(--line-gold)",
                  borderRadius: "12px",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                }}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: "var(--gold)" }}>
                    {c.coord}
                  </div>
                  <div className="mt-1 font-body text-sm" style={{ color: "var(--text)" }}>{c.label}</div>
                  <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.28em]" style={{ color: "var(--text-3)" }}>
                    {c.detail}
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </AnimatePresence>
      </div>

      {/* -------------- Educational method cards -------------- */}
      <div
        className="relative -mt-20 z-20 pb-24 md:pb-32"
        style={{ background: "linear-gradient(180deg, transparent 0%, var(--bg) 40%)" }}
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {INSPECTION_METHOD.map((m, i) => (
              <motion.div
                key={m.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="glass-card glass-reflect float-lift p-8 md:p-10"
                data-testid={`inspection-card-${i}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-11 h-11 flex items-center justify-center" style={{
                    borderRadius: "999px",
                    background: "rgba(198,168,124,0.08)",
                    border: "1px solid var(--line-gold)",
                  }}>
                    <m.Icon size={16} style={{ color: "var(--gold)" }} strokeWidth={1.5} />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "var(--text-3)" }}>
                    {m.n}
                  </div>
                </div>
                <h3 className="font-display" style={{ fontSize: "clamp(1.4rem, 1.8vw, 1.9rem)", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                  {m.title}
                </h3>
                <p className="mt-4 font-body" style={{ color: "var(--text-2)", fontSize: 15, lineHeight: 1.65 }}>
                  {m.body}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Attribution — good citizenship w/ Pexels */}
          <div className="mt-10 text-center font-mono text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--text-3)" }}>
            Sample surveillance imagery · Actual client footage available on request
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------- CSS/Canvas Fallback --------------- */
function FallbackCinematic() {
  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="drone-fallback">
      {/* Sky wash */}
      <div className="absolute inset-0" style={{
        background: `
          linear-gradient(180deg, #06070c 0%, #0d1018 40%, #1a1a1e 65%, #2a2018 100%),
          radial-gradient(400px 200px at 78% 30%, rgba(232,217,197,0.22), transparent 70%)
        `,
      }} />
      {/* Rising sun bloom */}
      <div className="absolute" style={{
        top: "22%", right: "18%",
        width: 320, height: 320,
        background: "radial-gradient(circle, rgba(232,217,197,0.45), rgba(198,168,124,0.14) 40%, transparent 70%)",
        filter: "blur(2px)",
        mixBlendMode: "screen",
      }} />
      {/* Distant building silhouettes — 3 layers, subtle parallax */}
      <svg viewBox="0 0 1600 900" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMax slice">
        <defs>
          <linearGradient id="bldg-far" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a1c22" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#050506" />
          </linearGradient>
          <linearGradient id="bldg-mid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22232a" />
            <stop offset="100%" stopColor="#0a0a0d" />
          </linearGradient>
          <linearGradient id="bldg-near" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2c2d34" />
            <stop offset="100%" stopColor="#0a0a0d" />
          </linearGradient>
        </defs>
        {/* Far layer */}
        <g fill="url(#bldg-far)">
          <rect x="0"    y="580" width="180" height="320" />
          <rect x="200"  y="620" width="140" height="280" />
          <rect x="360"  y="560" width="220" height="340" />
          <rect x="600"  y="600" width="170" height="300" />
          <rect x="790"  y="540" width="240" height="360" />
          <rect x="1050" y="610" width="180" height="290" />
          <rect x="1250" y="570" width="200" height="330" />
          <rect x="1470" y="620" width="140" height="280" />
        </g>
        {/* Mid layer */}
        <g fill="url(#bldg-mid)">
          <rect x="0"    y="670" width="260" height="230" />
          <rect x="280"  y="700" width="220" height="200" />
          <rect x="520"  y="660" width="300" height="240" />
          <rect x="840"  y="690" width="240" height="210" />
          <rect x="1100" y="670" width="280" height="230" />
          <rect x="1400" y="700" width="200" height="200" />
        </g>
        {/* Near foreground */}
        <g fill="url(#bldg-near)">
          <rect x="-40"  y="760" width="380" height="150" />
          <rect x="360"  y="780" width="440" height="140" />
          <rect x="820"  y="770" width="360" height="150" />
          <rect x="1200" y="790" width="500" height="130" />
        </g>
        {/* Little window lights */}
        {[...Array(80)].map((_, i) => {
          const x = 20 + (i * 20) + Math.sin(i * 1.7) * 10;
          const y = 610 + ((i * 37) % 260);
          return <rect key={i} x={x} y={y} width="3" height="4" fill="rgba(232,217,197,0.35)" />;
        })}
      </svg>
      {/* Floating dust motes */}
      {[...Array(28)].map((_, i) => {
        const left = (i * 13.7) % 100;
        const top = 20 + ((i * 23) % 60);
        const dur = 6 + (i % 5);
        return (
          <motion.div
            key={i}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: `${left}%`, top: `${top}%`,
              width: 2 + (i % 3), height: 2 + (i % 3),
              background: "rgba(232,217,197,0.7)",
              filter: "blur(0.5px)",
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.2, 0.7, 0.2],
            }}
            transition={{
              duration: dur,
              repeat: Infinity,
              ease: "easeInOut",
              delay: (i * 0.3) % dur,
            }}
          />
        );
      })}
    </div>
  );
}
